import React, { useState } from 'react';
import { images as imageUtils } from '../../../utils/api';
import { LotDetailsModal } from './LotDetailsModal';
import { createPortal } from 'react-dom';

const LotImage = React.memo(function LotImage({ imageList }) {
  const imageRef = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(null);
  const hasImage = Boolean(imageList?.length);

  React.useEffect(() => {
    if (hasImage) {
      // Sort images by display_order and use the first one
      const sortedImages = [...imageList].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setImageUrl(imageUtils.getImage(sortedImages[0].thumbnail_path, sortedImages[0].source));
    }
  }, [hasImage, imageList]);

  React.useEffect(() => {
    let observer;
    const currentImage = imageRef.current;

    if (currentImage && !isVisible) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin: '50px' }
      );

      observer.observe(currentImage);
    }

    return () => {
      if (observer && currentImage) {
        observer.disconnect();
      }
    };
  }, [isVisible]);

  return (
    <div 
      ref={imageRef}
      className="h-40 w-full bg-gray-100 rounded border border-gray-200 p-2 flex items-center justify-center"
    >
      {imageUrl && isVisible ? (
        <img
          src={imageUrl}
          alt="Lot Image"
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      ) : (
        <span className="text-gray-400">
          {hasImage ? 'Loading...' : 'No Image'}
        </span>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Compare images after sorting by display_order
  const getSortedFirstImage = (list) => {
    if (!list?.length) return null;
    return [...list].sort((a, b) => (a.display_order || 0) - (b.display_order || 0))[0];
  };
  const prevFirst = getSortedFirstImage(prevProps.imageList);
  const nextFirst = getSortedFirstImage(nextProps.imageList);
  return prevFirst?.thumbnail_path === nextFirst?.thumbnail_path;
});

const TruncatedText = React.memo(function TruncatedText({ text, className }) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const containerRef = React.useRef(null);
  // Only show tooltip if text is actually truncated
  const isTextTruncated = React.useCallback(() => {
    const element = containerRef.current;
    if (!element) return false;
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    if (isTextTruncated()) {
      setShowTooltip(true);
    }
  }, [text, isTextTruncated]);

  return (
    <div 
      ref={containerRef}
      className={`${className} relative`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => {
        setShowTooltip(false);
      }}
    >
      {text}
      {showTooltip && createPortal(
        <div 
          className="fixed transform bg-gray-800 text-white text-sm rounded-md px-2 py-1 shadow-lg"
          style={{
            zIndex: 999999999,
            maxWidth: '300px',
            wordBreak: 'break-word',
            left: containerRef.current ? 
              containerRef.current.getBoundingClientRect().left + (containerRef.current.offsetWidth / 2) : 0,
            top: containerRef.current ? 
              containerRef.current.getBoundingClientRect().bottom + 8 : 0,
            transform: 'translateX(-50%)'
          }}
        >
          <div 
            className="absolute left-1/2 -translate-x-1/2 -top-2 border-4 border-transparent border-b-gray-800"
          />
          {text}
        </div>,
        document.body
      )}
    </div>
  );
});

// Separate component for tracking state
const TrackingButton = React.memo(function TrackingButton({ 
  lotId, 
  isTracked, 
  isAuctionSelected, 
  onSelect, 
  onDeselect, 
  onSelectAuction
}) {
  const [isPending, setPending] = useState(false);

  const handleClick = React.useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    
    setPending(true);
    try {
      if (isTracked) {
        await onDeselect(lotId);
      } else if (isAuctionSelected) {
        await onSelect(lotId);
      } else {
        // If auction isn't selected, open auction selection modal with the lot ID to track
        onSelectAuction(lotId);
      }
    } finally {
      setPending(false);
    }
  }, [lotId, isTracked, isAuctionSelected, onSelect, onDeselect, onSelectAuction, isPending]);

  return (
    <button
      onClick={handleClick}
      disabled={isPending}
      className={`
        w-full px-3 py-1 text-sm font-medium rounded-md transition-all duration-150
        ${isTracked
          ? "text-red-600 hover:text-red-800 border border-red-600 hover:bg-red-50"
          : "text-blue-600 hover:text-blue-800 border border-blue-600 hover:bg-blue-50"
        }
        ${isPending ? 'opacity-75 cursor-not-allowed' : ''}
      `}
    >
      {isPending ? (
        <span className="inline-flex items-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {isTracked ? 'Stopping...' : 'Tracking...'}
        </span>
      ) : isTracked ? (
        'Stop Tracking'
      ) : (
        'Track Lot'
      )}
    </button>
  );
}, (prevProps, nextProps) => {
  // Always re-render when tracking state changes
  if (prevProps.isTracked !== nextProps.isTracked) return false;
  
  // Otherwise, check other props
  return (
    prevProps.lotId === nextProps.lotId &&
    prevProps.isAuctionSelected === nextProps.isAuctionSelected
  );
});

// Split the card into static and dynamic parts
// Extract image section to prevent re-renders from other content changes
const LotImageSection = React.memo(function LotImageSection({ images }) {
  return (
    <div className="mb-2 flex-none">
      <LotImage imageList={images} />
    </div>
  );
}, (prev, next) => JSON.stringify(prev.images) === JSON.stringify(next.images));

const StaticLotContent = React.memo(function StaticLotContent({ 
  lot, 
  auctionCurrency, 
  certServices 
}) {
  return (
    <>
      <div className="mb-2 text-sm font-medium text-gray-600">
        Lot {lot.lot_number}
      </div>
      <LotImageSection images={lot.images} />
      
      <div className="flex flex-col flex-grow">
        <div className="space-y-2">
          <div className="min-h-[3.5rem]">
            <TruncatedText
              text={lot.metadata?.translated_title || lot.metadata?.raw_title || `${lot.country} ${lot.year}${lot.mint_mark ? `-${lot.mint_mark}` : ''} ${lot.denomination}${lot.variety ? ` ${lot.variety}` : ''}`}
              className="text-base font-medium text-gray-900 line-clamp-2"
            />
          </div>
          <div className="h-5 flex items-center">
            <TruncatedText
              text={`${lot.certification_service_id ? (certServices[lot.certification_service_id] || 'Cert') + ' ' : ''}${lot.grade || ''}`}
              className="text-sm text-gray-600"
            />
            {lot.cac_sticker && lot.cac_sticker !== 'none' && (
              <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                lot.cac_sticker === 'gold' 
                  ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                  : 'bg-green-100 text-green-800 border border-green-200'
              }`}>
                CAC
              </span>
            )}
          </div>
        </div>

        <div className="mt-auto pt-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-900">
              Open Bid: {lot.opening_bid ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: auctionCurrency,
                minimumFractionDigits: Number.isInteger(lot.opening_bid) ? 0 : 2,
                maximumFractionDigits: 2
              }).format(lot.opening_bid) : '-'}
            </span>
            {lot.winning_bid && (
              <span className="text-sm font-medium text-green-600">
                Hammer: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: auctionCurrency,
                  minimumFractionDigits: Number.isInteger(lot.winning_bid) ? 0 : 2,
                  maximumFractionDigits: 2
                }).format(lot.winning_bid)}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}, (prevProps, nextProps) => {
  const { lot: prevLot, ...prevRest } = prevProps;
  const { lot: nextLot, ...nextRest } = nextProps;
  
  return (
    prevLot.lot_number === nextLot.lot_number &&
    JSON.stringify(prevLot.images) === JSON.stringify(nextLot.images) &&
    prevLot.title === nextLot.title &&
    prevLot?.metadata?.raw_title === nextLot?.metadata?.raw_title &&
    prevLot?.metadata?.translated_title === nextLot?.metadata?.translated_title &&
    prevLot.year === nextLot.year &&
    prevLot.denomination === nextLot.denomination &&
    prevLot.variety === nextLot.variety &&
    prevLot.grade === nextLot.grade &&
    prevLot.certification_number === nextLot.certification_number &&
    prevLot.certification_service_id === nextLot.certification_service_id &&
    prevLot.opening_bid === nextLot.opening_bid &&
    prevRest.auctionCurrency === nextRest.auctionCurrency
  );
});

// Separate tracking section to prevent full card re-renders
const TrackingSection = React.memo(function TrackingSection({
  lotId,
  isTracked,
  isAuctionSelected,
  onSelect,
  onDeselect,
  onSelectAuction
}) {
  return (
    <div className="mt-2 tracking-section">
      <TrackingButton
        lotId={lotId}
        isTracked={isTracked}
        isAuctionSelected={isAuctionSelected}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onSelectAuction={onSelectAuction}
      />
    </div>
  );
}, (prev, next) => (
  prev.lotId === next.lotId &&
  prev.isTracked === next.isTracked &&
  prev.isAuctionSelected === next.isAuctionSelected
));

const LotCard = React.memo(function LotCard({ 
  lot, 
  auctionId, 
  onSelect, 
  onDeselect, 
  auctionCurrency, 
  isAuctionSelected, 
  onOpenAuctionModal, 
  onSelectAuction,
  certServices
}) {
  return (
    <div 
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow h-full flex flex-col cursor-pointer"
      onClick={(e) => {
        // Don't open modal if clicking tracking section
        if (e.target.closest('button') || e.target.closest('.tracking-section')) return;
        onOpenAuctionModal(lot.id);
      }}
    >
      <StaticLotContent 
        lot={lot}
        auctionCurrency={auctionCurrency}
        certServices={certServices}
      />
      <TrackingSection
        lotId={lot.id}
        isTracked={lot.user_selection}
        isAuctionSelected={isAuctionSelected}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onSelectAuction={onSelectAuction}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Always re-render when tracking state changes
  if (prevProps.lot.user_selection !== nextProps.lot.user_selection) return false;
  
  // Otherwise, compare other props
  const { lot: prevLot, ...prevRest } = prevProps;
  const { lot: nextLot, ...nextRest } = nextProps;
  
  // Compare non-tracking lot properties
  const lotPropsEqual = 
    prevLot.id === nextLot.id &&
    prevLot.lot_number === nextLot.lot_number &&
    prevLot.title === nextLot.title &&
    prevLot?.metadata?.raw_title === nextLot?.metadata?.raw_title &&
    prevLot?.metadata?.translated_title === nextLot?.metadata?.translated_title &&
    prevLot.year === nextLot.year &&
    prevLot.denomination === nextLot.denomination &&
    prevLot.variety === nextLot.variety &&
    prevLot.grade === nextLot.grade &&
    prevLot.certification_number === nextLot.certification_number &&
    prevLot.certification_service_id === nextLot.certification_service_id &&
    prevLot.opening_bid === nextLot.opening_bid;
    
  // Compare images only if other props are equal
  return lotPropsEqual && 
    JSON.stringify(prevLot.images) === JSON.stringify(nextLot.images) &&
    prevRest.auctionCurrency === nextRest.auctionCurrency &&
    prevRest.certServices === nextRest.certServices;
}
);

export { LotCard, TrackingButton };
